import { useParams, NavLink } from 'react-router-dom'
import {
  Terminal,
  BookOpen,
  Code,
  Package,
  Shield,
  Layers,
  Plug,
  User,
  FileJson,
} from 'lucide-react'

const DOCS_SECTIONS = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    icon: BookOpen,
    content: `
# Getting Started with Xarlie

Xarlie is a registry and marketplace for MCP (Model Context Protocol) skills. Think of it as the App Store for AI skills -- you can discover, install, and manage skills that give AI assistants like Claude new capabilities.

## What is MCP?

MCP (Model Context Protocol) is an open standard that lets AI assistants connect to external skills and data sources. For example, an MCP skill might let Claude check the weather, query a database, or manage your GitHub issues.

Xarlie makes it easy to find trusted MCP skills, install them, and export configurations for your favorite AI client.

## Browse Skills on the Web

The fastest way to get started is to browse the skill registry at [/browse](/browse). You can:

- **Search** by name or keyword
- **Filter** by capability, trust tier, or tag
- **View details** for any skill including its description, skills, endpoints, and publisher info
- **See trust badges** so you know which skills have been reviewed and approved

## Install the CLI

For publishing skills or managing subscriptions from your terminal, install the Xarlie CLI. Visit the [Downloads page](/downloads) for prebuilt binaries for macOS, Windows, and Linux.

Or install from source (requires Go 1.22+):

\`\`\`bash
go install github.com/xarlie-io/xarlie/cmd/xarlie@latest

# Verify the installation
xarlie --help
\`\`\`

### CLI Global Flags

\`\`\`
--api-url    Xarlie API URL (default: https://api.xarlie.io)
--verbose    Enable verbose/debug logging
\`\`\`

## Quick Start: Find and Use a Skill

### 1. Search for a skill

\`\`\`bash
xarlie search -q "weather"
\`\`\`

### 2. View skill details on the web

Open the skill's page at \`https://xarlie.io/skills/<slug>\` to see its full description, capabilities, and how to configure it.

### 3. Create a collection and export

\`\`\`bash
# Create a collection
xarlie collections create "My Skills" --org your-org-id

# Add a skill to the collection
xarlie collections add <collection-id> weather-skill

# Export for Claude Desktop
xarlie collections export <collection-id> --target claude-desktop -o config.json
\`\`\`

## Create a Publisher Account

If you want to publish your own MCP skills, you'll need a publisher account:

1. Go to [/publisher/signup](/publisher/signup)
2. Fill in your display name, email, password, and account type (individual or organization)
3. Log in at [/publisher/login](/publisher/login)
4. Use the Publisher Dashboard to create and manage your packages

## Next Steps

- [User Guide](/docs/user-guide) -- Learn how to browse and use skills
- [CLI Reference](/docs/cli) -- Full command reference
- [Publishing Guide](/docs/publishing) -- Publish your own skills
- [MCP Integrations](/docs/integrations) -- Set up skills with Claude Desktop, Cursor, and more
    `,
  },
  {
    id: 'user-guide',
    title: 'User Guide',
    icon: User,
    content: `
# User Guide

This guide covers everything you can do as a Xarlie user.

## Browsing Skills

### Search and Filter

Visit [/browse](/browse) to explore the skill registry. You can:

- **Search by keyword** -- Type in the search box to find skills by name, description, or capabilities
- **Filter by trust tier** -- Choose "Approved" to see only reviewed skills, or "All" to see everything
- **Filter by capability** -- Find skills that can do specific things (e.g., weather, code analysis, translation)
- **Filter by tag** -- Browse by category tags like "utility", "developer", "productivity"

### Skill Detail Pages

Click any skill to see its full detail page at \`/skills/<slug>\`. Here you'll find:

- **Overview** -- Name, summary, and full description
- **Trust badges** -- Approved, Verified Publisher, Signed Release, etc.
- **Capabilities** -- What the skill can do
- **Tags** -- Category tags
- **Skills list** -- Each individual skill function with its input/output schema
- **Endpoints** -- Where the MCP server is hosted
- **Publisher info** -- Who created the skill and their verification status
- **Links** -- Repository URL, documentation URL, license info

### Creator Pages

Click a publisher's name to visit their creator page at \`/creators/<publisher-id>\`. This shows their profile and all their published skills.

## Collections

Collections let you group multiple skills together for easy management and export.

### Browse Public Collections

Visit [/collections](/collections) to see public collections created by other users. You can view collection contents and export configurations directly.

### Create Your Own Collection

1. Go to [/collections](/collections) and click "New Collection"
2. Give it a name and description
3. Choose whether it's public or private
4. Add skills by searching and clicking "Add"

### Export a Collection

Collections can be exported in three formats:

- **Claude Desktop** -- Ready-to-use configuration for Claude Desktop's config file
- **MCP Settings** -- Generic MCP settings format
- **JSON** -- Raw JSON export

Click the "Export" button on any collection to download the configuration.

### Managing Collections

- **Add skills** -- Use the "Add Skill" button to search and add packages
- **Remove skills** -- Click the remove button next to any skill in the collection
- **Change visibility** -- Toggle between public and private
- **Delete** -- Remove the collection entirely

## Publisher Accounts

### Signing Up

1. Visit [/publisher/signup](/publisher/signup)
2. Enter your email, password (minimum 8 characters), and display name
3. Choose your account type: **Individual** or **Organization**
4. Optionally add your website URL
5. If you have a beta access token, enter it to link your account

### Logging In

Visit [/publisher/login](/publisher/login) with your email and password.

### Publisher Dashboard

Once logged in, you'll see your dashboard at [/publisher](/publisher) with:

- **Overview stats** -- Package count, total views
- **Your packages** -- List of all your published packages
- **Quick actions** -- Create new package, manage settings

### Publisher Settings

At [/publisher/settings](/publisher/settings) you can update your:

- Display name
- Email address
- Website URL
- Password
- Account type (Individual / Organization)
    `,
  },
  {
    id: 'cli',
    title: 'CLI Reference',
    icon: Terminal,
    content: `
# CLI Reference

Complete reference for the Xarlie command-line interface.

## Installation

\`\`\`bash
go install github.com/xarlie-io/xarlie/cmd/xarlie@latest
\`\`\`

## Global Flags

| Flag | Short | Description |
|------|-------|-------------|
| --api-url | | API base URL (default: https://api.xarlie.io) |
| --verbose | -v | Enable debug logging |

## Publisher Commands

### xarlie init

Create a new \`xarlie.json\` manifest template in the current directory.

\`\`\`bash
xarlie init
xarlie init -o path/to/manifest.json
\`\`\`

| Flag | Short | Default | Description |
|------|-------|---------|-------------|
| --output | -o | xarlie.json | Output file path |

### xarlie validate

Validate your manifest file against the Xarlie schema. Checks that all required fields are present, permissions are valid, and skills are properly defined.

\`\`\`bash
xarlie validate
xarlie validate -f my-manifest.json
\`\`\`

| Flag | Short | Default | Description |
|------|-------|---------|-------------|
| --file | -f | xarlie.json | Path to manifest file |

### xarlie dev

Test your MCP endpoint by calling its \`tools/list\` method and comparing the response with your manifest. Useful during development to make sure your server and manifest stay in sync.

\`\`\`bash
xarlie dev --endpoint http://localhost:3000/mcp
xarlie dev -e http://localhost:3000/mcp -f my-manifest.json
\`\`\`

| Flag | Short | Default | Description |
|------|-------|---------|-------------|
| --endpoint | -e | (required) | MCP endpoint URL to test |
| --file | -f | xarlie.json | Path to manifest file |

### xarlie sign

Sign your manifest with an Ed25519 key. You can generate a new keypair or use an existing key.

\`\`\`bash
# Generate a new keypair and sign
xarlie sign --generate-key

# Sign with an existing key
xarlie sign -k my-private.key

# Custom output path
xarlie sign -o manifest.sig
\`\`\`

| Flag | Short | Default | Description |
|------|-------|---------|-------------|
| --file | -f | xarlie.json | Path to manifest file |
| --key | -k | xarlie.key | Path to Ed25519 private key |
| --output | -o | xarlie.json.sig | Signature output path |
| --generate-key | | false | Generate a new Ed25519 keypair |

When using \`--generate-key\`, two files are created:
- \`xarlie.key\` -- Private key (keep this secret)
- \`xarlie.pub\` -- Public key (share this / upload to your publisher profile)

### xarlie publish

Publish a new version of your package to the registry.

\`\`\`bash
xarlie publish --package-id <your-package-id>
xarlie publish --package-id <your-package-id> -f custom-manifest.json
\`\`\`

| Flag | Short | Default | Description |
|------|-------|---------|-------------|
| --package-id | | (required) | Your package's ID |
| --file | -f | xarlie.json | Path to manifest file |

### xarlie submit-review

Submit a published version for review by the Xarlie team. Once approved, your skill gets the "Approved" badge and higher visibility in search.

\`\`\`bash
xarlie submit-review --package-id <id> --version 1.0.0
xarlie submit-review --package-id <id> --version 1.0.0 --notes "First release"
\`\`\`

| Flag | Short | Default | Description |
|------|-------|---------|-------------|
| --package-id | | (required) | Your package's ID |
| --version | | (required) | Version to submit |
| --notes | | | Optional notes for the reviewer |

## User Commands

### xarlie search

Search the Xarlie registry for skills.

\`\`\`bash
xarlie search -q "github"
xarlie search --capability code.analysis
xarlie search --trust-tier approved
\`\`\`

| Flag | Short | Default | Description |
|------|-------|---------|-------------|
| --query | -q | | Search query text |
| --capability | | | Filter by capability |
| --trust-tier | | | Filter by trust tier (approved, unreviewed) |

### xarlie install

Subscribe to a package by its slug.

\`\`\`bash
xarlie install weather-skill --org <your-org-id>
xarlie install weather-skill --org <your-org-id> --plan pro
\`\`\`

| Flag | Short | Default | Description |
|------|-------|---------|-------------|
| --org | | (required) | Your organization ID |
| --plan | | free | Subscription plan (free or pro) |

### xarlie connect

Start an OAuth device flow to connect your account with a skill's external service (e.g., GitHub). Note: OAuth connections are coming in a future release.

\`\`\`bash
xarlie connect github-assistant --provider github --org <your-org-id>
\`\`\`

| Flag | Short | Default | Description |
|------|-------|---------|-------------|
| --org | | (required) | Your organization ID |
| --provider | | (required) | OAuth provider ID |
| --kind | | workspace | Connection type: workspace or user |

### xarlie subscriptions list

List your active subscriptions.

\`\`\`bash
xarlie subscriptions list
xarlie subscriptions list --org <org-id>
\`\`\`

| Flag | Short | Default | Description |
|------|-------|---------|-------------|
| --org | | | Filter by organization ID |

### xarlie connections list

List your OAuth connections.

\`\`\`bash
xarlie connections list
\`\`\`

### xarlie connections revoke

Revoke an OAuth connection. Note: OAuth connections are coming in a future release.

\`\`\`bash
xarlie connections revoke <connection-id>
\`\`\`

### xarlie sandbox run

Run a skill in the Xarlie sandbox for testing.

\`\`\`bash
xarlie sandbox run weather-skill --tool weather.current
xarlie sandbox run weather-skill --tool weather.current --input request.json
\`\`\`

| Flag | Short | Default | Description |
|------|-------|---------|-------------|
| --tool | | (required) | Name of the skill to run |
| --input | | | Path to a JSON file with input arguments |

### xarlie collections create

Create a new skill collection.

\`\`\`bash
xarlie collections create "My Dev Skills" --org <org-id>
xarlie collections create "My Dev Skills" --org <org-id> --description "Skills for development"
\`\`\`

| Flag | Short | Default | Description |
|------|-------|---------|-------------|
| --org | | (required) | Organization ID |
| --description | | | Collection description |

### xarlie collections add

Add a skill to a collection.

\`\`\`bash
xarlie collections add <collection-id> weather-skill
xarlie collections add <collection-id> weather-skill --version ">=1.0.0"
\`\`\`

| Flag | Short | Default | Description |
|------|-------|---------|-------------|
| --version | | * | Version constraint |

### xarlie collections export

Export a collection configuration.

\`\`\`bash
# Export for Claude Desktop
xarlie collections export <collection-id> --target claude-desktop -o config.json

# Export as MCP settings
xarlie collections export <collection-id> --target mcp-settings

# Export as raw JSON
xarlie collections export <collection-id> --target json
\`\`\`

| Flag | Short | Default | Description |
|------|-------|---------|-------------|
| --target | | json | Export format: claude-desktop, mcp-settings, or json |
| --output | -o | | Output file path (prints to stdout if omitted) |
    `,
  },
  {
    id: 'publishing',
    title: 'Publishing Guide',
    icon: Package,
    content: `
# Publishing Guide

This guide walks you through publishing your own MCP skills to Xarlie.

## Prerequisites

Before you begin, you need:

1. **An MCP-compatible server** -- Your skill must implement the MCP protocol (specifically the \`tools/list\` and \`tools/call\` methods)
2. **The Xarlie CLI** -- Install with \`go install github.com/xarlie-io/xarlie/cmd/xarlie@latest\`
3. **A publisher account** -- Sign up at [/publisher/signup](/publisher/signup)

## Step 1: Create Your Publisher Account

1. Go to [/publisher/signup](/publisher/signup)
2. Enter your details:
   - **Display Name** -- Your name or organization name (this is public)
   - **Email** -- Your email address for login and notifications
   - **Password** -- At least 8 characters
   - **Type** -- "Individual" or "Organization"
   - **Website** -- Optional link to your website
3. After signup, log in to your Publisher Dashboard at [/publisher](/publisher)

## Step 2: Create a Package

### From the Web

1. Go to your Publisher Dashboard
2. Click "New Package"
3. Fill in the 3-step form:
   - **Basic Info** -- Name, slug (URL-friendly identifier), and summary
   - **Capabilities** -- What your skill can do (e.g., \`weather.current\`, \`code.analyze\`)
   - **Details** -- Full description, tags, repository URL, documentation URL

### From the CLI

Create a manifest file first:

\`\`\`bash
xarlie init
\`\`\`

## Step 3: Write Your Manifest

The \`xarlie.json\` manifest describes your package. Here is a complete example:

\`\`\`json
{
  "slug": "my-weather-skill",
  "name": "My Weather Skill",
  "version": "1.0.0",
  "mcp_api_version": "2025-06-18",
  "summary": "Get weather forecasts for any location",
  "description": "A full-featured weather skill.",
  "capabilities": ["weather.current", "weather.forecast"],
  "tags": ["weather", "utility"],
  "permissions": {
    "version": "v1",
    "data_access": ["data.none"],
    "capabilities_access": ["sys.network.outbound"]
  },
  "tools": [
    {
      "tool_name": "weather.current",
      "title": "Current Weather",
      "description": "Get current weather for a location",
      "input_schema": {
        "type": "object",
        "properties": {
          "location": {
            "type": "string",
            "description": "City name or coordinates"
          }
        },
        "required": ["location"]
      }
    }
  ],
  "endpoints": [
    {
      "env": "prod",
      "transport": "http_streamable",
      "base_url": "https://my-server.com/mcp",
      "auth_mode": "none"
    }
  ]
}
\`\`\`

## Step 4: Validate Your Manifest

\`\`\`bash
xarlie validate
\`\`\`

This checks that all required fields are present, permissions are valid, and skill schemas are well-formed.

## Step 5: Test with Dev Mode

Before publishing, test that your MCP server matches your manifest:

\`\`\`bash
xarlie dev --endpoint http://localhost:3000/mcp
\`\`\`

This calls your server's \`tools/list\` method and compares the response with your manifest.

## Step 6: Sign Your Release (Optional)

Signing adds the "Signed Release" badge and proves the manifest hasn't been tampered with.

\`\`\`bash
# First time: generate a keypair
xarlie sign --generate-key

# Sign the manifest
xarlie sign
\`\`\`

## Step 7: Publish

\`\`\`bash
xarlie publish --package-id <your-package-id>
\`\`\`

Find your package ID in the Publisher Dashboard after creating the package.

## Step 8: Submit for Review

To get the "Approved" badge and higher search visibility:

\`\`\`bash
xarlie submit-review --package-id <id> --version 1.0.0
\`\`\`

After submission:
1. Automated checks run (schema validation, endpoint health)
2. The Xarlie team reviews your skill manually
3. Status updates appear in your dashboard
4. If changes are requested, make the fixes and resubmit

## Publishing Updates

To publish a new version:

1. Update the \`version\` field in your \`xarlie.json\`
2. Run \`xarlie validate\` to verify
3. Run \`xarlie publish --package-id <your-package-id>\`
4. Optionally submit the new version for review
    `,
  },
  {
    id: 'manifest',
    title: 'Manifest Schema',
    icon: FileJson,
    content: `
# Manifest Schema

The \`xarlie.json\` manifest file defines your MCP skill package. This page documents every field.

## Top-Level Fields

| Field | Type | Required | Description |
|-------|------|----------|-------------|
| slug | string | Yes | Unique URL-friendly identifier (e.g., "my-weather-skill") |
| name | string | Yes | Human-readable display name |
| version | string | Yes | Semantic version (e.g., "1.0.0") |
| mcp_api_version | string | Yes | MCP API version (e.g., "2025-06-18") |
| summary | string | No | Short one-line description |
| description | string | No | Full description |
| capabilities | string[] | Yes | List of capability identifiers |
| tags | string[] | No | Category tags for search |
| permissions | object | Yes | Permission declarations |
| tools | Tool[] | Yes | Tool definitions |
| endpoints | Endpoint[] | Yes | MCP server endpoints |

## Permissions Object

\`\`\`json
{
  "permissions": {
    "version": "v1",
    "data_access": ["data.none"],
    "capabilities_access": ["sys.network.outbound"],
    "integrations": [
      {
        "provider": "github",
        "access": "read",
        "scopes": ["repo", "user:email"]
      }
    ]
  }
}
\`\`\`

### Data Access Levels

| Level | Description |
|-------|-------------|
| data.none | No user data is accessed |
| data.user_content | Accesses user-generated content |
| data.pii | Accesses personally identifiable information |
| data.financial | Accesses financial data |
| data.credentials | Accesses secrets or credentials |

### Capabilities Access

| Capability | Description |
|------------|-------------|
| sys.network.outbound | Makes outbound HTTP requests |
| sys.filesystem.read | Reads files from the filesystem |
| sys.filesystem.write | Writes files to the filesystem |
| sys.exec | Executes shell commands |

### Integration Providers

Supported providers: \`github\`, \`google\`, \`slack\`, \`jira\`, \`notion\`, \`stripe\`, \`shopify\`, \`public_web\`, \`custom\`

## Tool Object

Each tool represents a single function your MCP server provides.

\`\`\`json
{
  "tool_name": "weather.current",
  "title": "Current Weather",
  "description": "Get the current weather conditions for a location",
  "capabilities": ["weather.current"],
  "input_schema": {
    "type": "object",
    "properties": {
      "location": {
        "type": "string",
        "description": "City name or lat,lng coordinates"
      },
      "units": {
        "type": "string",
        "enum": ["celsius", "fahrenheit"],
        "description": "Temperature units"
      }
    },
    "required": ["location"]
  }
}
\`\`\`

| Field | Type | Required | Description |
|-------|------|----------|-------------|
| tool_name | string | Yes | Unique name for the tool |
| title | string | Yes | Human-readable title |
| description | string | Yes | What the tool does |
| capabilities | string[] | No | Capabilities this tool uses |
| input_schema | object | Yes | JSON Schema for the tool's input |

## Endpoint Object

Endpoints tell Xarlie where your MCP server is hosted.

\`\`\`json
{
  "env": "prod",
  "transport": "http_streamable",
  "base_url": "https://my-server.com/mcp",
  "auth_mode": "api_key",
  "region": "us-east-1"
}
\`\`\`

| Field | Type | Required | Description |
|-------|------|----------|-------------|
| env | string | No | Environment: prod, staging, dev |
| transport | string | Yes | Transport protocol (see below) |
| base_url | string | Yes | Full URL of your MCP server |
| auth_mode | string | Yes | Authentication: none, api_key, oauth2 |
| region | string | No | Deployment region for latency info |

### Transport Types

| Transport | Description |
|-----------|-------------|
| http_streamable | HTTP with streaming support (recommended) |
| http_sse | HTTP with Server-Sent Events |
| websocket | WebSocket connection |
| stdio | Standard input/output (for local tools) |
    `,
  },
  {
    id: 'api',
    title: 'API Reference',
    icon: Code,
    content: `
# API Reference

The Xarlie REST API lets you interact with the registry programmatically.

## Base URL

\`\`\`
https://api.xarlie.io
\`\`\`

## Authentication

Endpoints marked with "Auth Required" need a Bearer token:

\`\`\`
Authorization: Bearer <your-jwt-token>
\`\`\`

Get a token by logging in as a publisher (\`POST /v1/publisher/login\`).

## Public Endpoints

### Search Packages

\`\`\`
GET /v1/packages/search
\`\`\`

| Parameter | Type | Description |
|-----------|------|-------------|
| q | string | Search query |
| capability | string | Filter by capability |
| trust_tier | string | Filter: approved or unreviewed |
| tag | string | Filter by tag |
| limit | number | Results per page (max 100) |
| cursor | string | Pagination cursor |

**Example:**

\`\`\`bash
curl "https://api.xarlie.io/v1/packages/search?q=weather&trust_tier=approved"
\`\`\`

### Get Package

\`\`\`
GET /v1/packages/{package_id}
\`\`\`

### Resolve Package by Slug

\`\`\`
GET /v1/resolve?slug={slug}
\`\`\`

### List Versions

\`\`\`
GET /v1/packages/{package_id}/versions
\`\`\`

### List Tools in a Version

\`\`\`
GET /v1/packages/{package_id}/versions/{version}/tools
\`\`\`

### Public Collections

\`\`\`
GET /v1/collections/public
GET /v1/collections/public/{collection_id}
\`\`\`

### Health Check

\`\`\`
GET /health
\`\`\`

## Publisher Endpoints

### Sign Up

\`\`\`
POST /v1/publisher/signup
\`\`\`

\`\`\`json
{
  "email": "you@example.com",
  "password": "your-password",
  "display_name": "Your Name",
  "type": "individual",
  "website_url": "https://yoursite.com"
}
\`\`\`

### Log In

\`\`\`
POST /v1/publisher/login
\`\`\`

\`\`\`json
{
  "email": "you@example.com",
  "password": "your-password"
}
\`\`\`

Returns a JWT token and publisher profile.

## Authenticated Endpoints

### Create Package (Auth Required)

\`\`\`
POST /v1/packages
\`\`\`

\`\`\`json
{
  "slug": "my-skill",
  "name": "My Skill",
  "summary": "A useful skill",
  "description": "Full description here",
  "capabilities": ["example.action"],
  "tags": ["utility"]
}
\`\`\`

### Publish Version (Auth Required)

\`\`\`
POST /v1/packages/{package_id}/versions
\`\`\`

### Upload Signature (Auth Required)

\`\`\`
POST /v1/packages/{package_id}/versions/{version}/signature
\`\`\`

\`\`\`json
{
  "signature": "base64-encoded-ed25519-signature",
  "key_id": "optional-key-uuid"
}
\`\`\`

### Submit for Review (Auth Required)

\`\`\`
POST /v1/packages/{package_id}/versions/{version}/submit_review
\`\`\`

### Get Review Status (Auth Required)

\`\`\`
GET /v1/reviews/{package_version_id}
\`\`\`

### Subscriptions (Auth Required)

\`\`\`
POST   /v1/subscriptions                              -- Create subscription
GET    /v1/subscriptions                              -- List subscriptions
POST   /v1/subscriptions/{subscription_id}/cancel     -- Cancel subscription
GET    /v1/subscriptions/{subscription_id}/entitlements -- List entitlements
\`\`\`

### Collections (Auth Required)

\`\`\`
GET    /v1/collections                          -- List your collections
POST   /v1/collections                          -- Create a collection
GET    /v1/collections/{collection_id}          -- Get collection details
PATCH  /v1/collections/{collection_id}          -- Update collection
DELETE /v1/collections/{collection_id}          -- Delete collection
POST   /v1/collections/{collection_id}/items    -- Add a skill
DELETE /v1/collections/{collection_id}/items/{package_id} -- Remove a skill
GET    /v1/collections/{collection_id}/export?target=claude-desktop -- Export
\`\`\`

### Waitlist

\`\`\`
POST /v1/waitlist                 -- Join the waitlist
POST /v1/waitlist/interests       -- Update skill interests
\`\`\`

### Beta Token Validation

\`\`\`
POST /v1/beta/validate
\`\`\`

\`\`\`json
{ "token": "xarlie_beta_..." }
\`\`\`

## Error Format

All errors follow this format:

\`\`\`json
{
  "error": "error_code",
  "message": "Human-readable description",
  "request_id": "uuid"
}
\`\`\`

| Code | Status | Description |
|------|--------|-------------|
| bad_request | 400 | Invalid input |
| unauthorized | 401 | Missing or invalid token |
| forbidden | 403 | Insufficient permissions |
| not_found | 404 | Resource not found |
| not_implemented | 501 | Feature not yet available |
    `,
  },
  {
    id: 'review',
    title: 'Review & Trust',
    icon: Shield,
    content: `
# Review & Trust

Xarlie uses a trust system to help users identify safe, high-quality tools.

## Trust Tiers

### Unreviewed

- Default tier for all newly published skills
- Shown with a yellow "Unreviewed" badge
- Fully functional -- users can still install and use the skill
- Users see the skill's declared permissions before use

### Approved

- Manually reviewed and approved by the Xarlie team
- Shown with a green "Approved" badge
- Higher visibility in search results
- Recommended for production and enterprise use

## Badges

Tools can earn multiple badges:

| Badge | Meaning |
|-------|---------|
| Approved | Passed the Xarlie manual review |
| Verified Publisher | Publisher's identity has been verified |
| Signed Release | Manifest is signed with an Ed25519 key |
| Healthy Endpoint | MCP server has 99%+ uptime |
| Validated Toolset | Server's tools/list response matches the manifest |

## How Review Works

### 1. Submit for Review

After publishing a version, submit it:

\`\`\`bash
xarlie submit-review --package-id <id> --version 1.0.0
\`\`\`

### 2. Automated Checks

The system automatically verifies:

- **Schema Validation** -- Manifest matches the required schema
- **Permission Validation** -- All permissions use valid strings
- **Endpoint Health** -- MCP server responds correctly
- **Toolset Matching** -- Server's tools/list matches the manifest

### 3. Manual Review

The Xarlie team reviews:

- **Permission Justification** -- Each permission is necessary
- **Data Handling** -- Follows declared data access levels
- **Security** -- No malicious patterns or unsafe behavior
- **Quality** -- Skills work as described

### 4. Possible Outcomes

- **Approved** -- Your skill gets the Approved badge
- **Changes Requested** -- Reviewer explains what needs to change
- **Suspended** -- Serious issues found; contact support

### Timeline

- Automated checks: within minutes
- Manual review: 1-5 business days
- Re-review after changes: 1-3 business days

### Getting Verified as a Publisher

Publisher verification is separate from tool review. To get the "Verified Publisher" badge:

1. Contact the Xarlie team
2. Provide proof of identity (organization website, GitHub profile, etc.)
3. Once verified, all your tools show the badge
    `,
  },
  {
    id: 'collections',
    title: 'Collections',
    icon: Layers,
    content: `
# Collections

Collections let you bundle multiple MCP skills together for easy installation and sharing.

## Why Collections?

If you use multiple MCP skills, collections let you:

- **Group related tools** (e.g., "My Dev Tools", "Marketing Stack")
- **Export configurations** for Claude Desktop and other MCP clients with one click
- **Share with others** by making your collection public
- **Version pin** tools to specific version constraints

## Creating a Collection

### On the Web

1. Visit [/collections](/collections)
2. Switch to the "My Collections" tab
3. Click "New Collection"
4. Enter a name and description
5. Choose if it should be public or private

### With the CLI

\`\`\`bash
xarlie collections create "My Dev Tools" \\
  --org <your-org-id> \\
  --description "Tools I use for development"
\`\`\`

## Adding Tools

### On the Web

1. Open your collection
2. Click "Add Skill"
3. Search for the skill you want
4. Click "Add" next to it

### With the CLI

\`\`\`bash
# Add a tool (latest version)
xarlie collections add <collection-id> weather-tool

# Add a tool with a version constraint
xarlie collections add <collection-id> github-assistant --version ">=1.0.0"
\`\`\`

## Exporting Collections

Export your entire tool stack as a ready-to-use configuration file.

### For Claude Desktop

\`\`\`bash
xarlie collections export <collection-id> --target claude-desktop -o config.json
\`\`\`

See the [MCP Integrations](/docs/integrations) section for detailed setup instructions.

### For MCP Settings

\`\`\`bash
xarlie collections export <collection-id> --target mcp-settings
\`\`\`

### As Raw JSON

\`\`\`bash
xarlie collections export <collection-id> --target json
\`\`\`

## Sharing Collections

Make your collection public to share it:

1. Open your collection on the web
2. Toggle the "Public" switch
3. Share the URL: \`https://xarlie.io/collections/<collection-id>\`

Anyone can view a public collection and export its configuration.
    `,
  },
  {
    id: 'integrations',
    title: 'MCP Integrations',
    icon: Plug,
    content: `
# MCP Integrations

This guide shows you how to use Xarlie tools with popular AI clients and MCP servers.

## Claude Desktop

Claude Desktop is Anthropic's desktop application with built-in MCP support.

### Setup

1. **Find tools on Xarlie** -- Browse [/browse](/browse) and pick the tools you want
2. **Create a collection** at [/collections](/collections) and add your chosen tools
3. **Export the collection** using the "Claude Desktop" export format
4. **Open Claude Desktop settings** -- Go to Settings > Developer > Edit Config
5. **Paste the configuration** into the \`mcpServers\` section of your \`claude_desktop_config.json\`

### Example Configuration

\`\`\`json
{
  "mcpServers": {
    "weather-skill": {
      "command": "npx",
      "args": ["-y", "mcp-remote", "https://weather-skill-server.com/mcp"]
    },
    "github-assistant": {
      "command": "npx",
      "args": ["-y", "mcp-remote", "https://github-assistant.com/mcp"]
    }
  }
}
\`\`\`

## Cursor

Cursor is an AI-powered code editor with MCP support.

### Setup

1. Open Cursor Settings (Cmd/Ctrl + ,)
2. Search for "MCP" in settings
3. Add your MCP server configuration
4. Restart Cursor to load the new tools

## VS Code with Copilot

VS Code supports MCP through the GitHub Copilot extension.

### Setup

1. Install the GitHub Copilot extension
2. Open VS Code Settings (Cmd/Ctrl + ,)
3. Search for "MCP" or "mcp.servers"
4. Add your tool configuration in \`settings.json\`:

\`\`\`json
{
  "github.copilot.chat.mcpServers": {
    "weather-skill": {
      "command": "npx",
      "args": ["-y", "mcp-remote", "https://weather-skill-server.com/mcp"]
    }
  }
}
\`\`\`

## Windsurf

Windsurf supports MCP servers through its settings. Navigate to the MCP configuration section and add your servers in the same JSON format as Claude Desktop.

## Popular MCP Servers

Here are some well-known MCP servers you can use alongside Xarlie tools.

### OpenClaw

OpenClaw is an open-source MCP skill registry and runtime. You can use OpenClaw skills alongside Xarlie skills.

- Browse OpenClaw's registry for community-built MCP skills
- Find a tool's MCP endpoint URL and add it to your client config

### MCP Filesystem Server

Gives AI assistants access to local files.

\`\`\`json
{
  "filesystem": {
    "command": "npx",
    "args": ["-y", "@modelcontextprotocol/server-filesystem", "/path/to/directory"]
  }
}
\`\`\`

### MCP GitHub Server

Interact with GitHub repositories, issues, and pull requests.

\`\`\`json
{
  "github": {
    "command": "npx",
    "args": ["-y", "@modelcontextprotocol/server-github"],
    "env": {
      "GITHUB_PERSONAL_ACCESS_TOKEN": "ghp_your_token_here"
    }
  }
}
\`\`\`

### MCP Postgres Server

Query PostgreSQL databases with read-only access.

\`\`\`json
{
  "postgres": {
    "command": "npx",
    "args": ["-y", "@modelcontextprotocol/server-postgres", "postgresql://user:pass@localhost/mydb"]
  }
}
\`\`\`

### MCP Slack Server

Read and send Slack messages.

\`\`\`json
{
  "slack": {
    "command": "npx",
    "args": ["-y", "@modelcontextprotocol/server-slack"],
    "env": {
      "SLACK_BOT_TOKEN": "xoxb-your-token",
      "SLACK_TEAM_ID": "T12345"
    }
  }
}
\`\`\`

### MCP Puppeteer Server

Control a web browser for scraping, screenshots, and testing.

\`\`\`json
{
  "puppeteer": {
    "command": "npx",
    "args": ["-y", "@modelcontextprotocol/server-puppeteer"]
  }
}
\`\`\`

## Combining Multiple Tools

The real power of MCP is combining multiple tools. For example, you could have Claude:

1. Use the **GitHub server** to read an issue
2. Use the **filesystem server** to read your codebase
3. Use a **code analysis tool** from Xarlie to suggest a fix
4. Use the **GitHub server** again to create a pull request

All tools are configured in the same config file and available to the AI simultaneously.

## Building Your Own MCP Server

Want to build and publish your own MCP skill?

1. Follow the [MCP specification](https://modelcontextprotocol.io) to build your server
2. Implement the \`tools/list\` and \`tools/call\` methods at minimum
3. Host your server (any cloud provider works)
4. Create a Xarlie manifest and [publish it](/docs/publishing)

Popular languages for building MCP servers:

- **TypeScript/Node.js** -- \`@modelcontextprotocol/sdk\`
- **Python** -- \`mcp\` package from PyPI
- **Go** -- MCP Go SDK
- **Rust** -- \`mcp-server\` crate
    `,
  },
]

export default function DocsPage() {
  const { section } = useParams<{ section: string }>()
  const currentSection =
    DOCS_SECTIONS.find((s) => s.id === section) || DOCS_SECTIONS[0]

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="lg:grid lg:grid-cols-4 lg:gap-8">
          {/* Sidebar */}
          <nav className="mb-8 lg:mb-0">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Documentation
            </h2>
            <ul className="space-y-1">
              {DOCS_SECTIONS.map((doc) => (
                <li key={doc.id}>
                  <NavLink
                    to={`/docs/${doc.id}`}
                    className={({ isActive }) =>
                      `flex items-center space-x-2 px-3 py-2 text-sm rounded-lg transition-colors ${
                        isActive ||
                        (doc.id === 'getting-started' && !section)
                          ? 'bg-primary-100 text-primary-700 font-medium'
                          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                      }`
                    }
                  >
                    <doc.icon className="w-4 h-4" />
                    <span>{doc.title}</span>
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>

          {/* Content */}
          <div className="lg:col-span-3">
            <div className="card p-8">
              <article className="prose prose-primary max-w-none">
                <div
                  dangerouslySetInnerHTML={{
                    __html: renderMarkdown(currentSection.content),
                  }}
                />
              </article>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// Simple markdown renderer
function renderMarkdown(content: string): string {
  return content
    .replace(
      /^### (.*$)/gim,
      '<h3 class="text-lg font-semibold mt-6 mb-2">$1</h3>'
    )
    .replace(
      /^## (.*$)/gim,
      '<h2 class="text-xl font-semibold mt-8 mb-3">$1</h2>'
    )
    .replace(/^# (.*$)/gim, '<h1 class="text-2xl font-bold mb-4">$1</h1>')
    .replace(/\*\*(.*?)\*\*/gim, '<strong>$1</strong>')
    .replace(
      /```(\w+)?\n([\s\S]*?)```/gim,
      '<pre class="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto my-4"><code>$2</code></pre>'
    )
    .replace(
      /`([^`]+)`/gim,
      '<code class="bg-gray-100 px-1.5 py-0.5 rounded text-sm">$1</code>'
    )
    .replace(
      /\[([^\]]+)\]\(([^)]+)\)/gim,
      '<a href="$2" class="text-primary-600 hover:underline">$1</a>'
    )
    .replace(/^\| (.*) \|$/gim, (_match: string, content: string) => {
      const cells = content.split(' | ')
      return `<tr>${cells
        .map(
          (c: string) =>
            `<td class="border border-gray-200 px-3 py-2">${c.trim()}</td>`
        )
        .join('')}</tr>`
    })
    .replace(/^- (.*$)/gim, '<li class="ml-4 mb-1">$1</li>')
    .replace(
      /^\d+\. (.*$)/gim,
      '<li class="ml-4 mb-1 list-decimal">$1</li>'
    )
    .replace(/\n\n/gim, '</p><p class="mb-4">')
    .replace(/\n/gim, '<br/>')
}
